import {
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { BetRecordScrollerArrow } from './svg';
import './index.scss';
import { GameType } from '@/models/games/enums/GameType';
import { ScreenContext } from '@/contexts/ScreenContext';

type BetRecordScrollerProps = {
    gameType: GameType;
    children?: ReactNode;
};
export const BetRecordScroller = (props: BetRecordScrollerProps) => {
    const dragState = useRef({
        isDown: false,
        startX: 0,
        scrollLeft: 0,
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const { isLandscape } = useContext(ScreenContext);
    const [isOverflow, setIsOverflow] = useState(false);
    const [isScrollStart, setIsScrollStart] = useState(false);
    const [isScrollEnd, setIsScrollEnd] = useState(false);
    const [scrollerCN, setScrollerCN] = useState(
        `scroll-content gt-${props.gameType}`
    );
    const handleScroll = useCallback(() => {
        const containerElement = containerRef.current;
        if (!containerElement) return;
        const content = containerElement.firstElementChild as HTMLDivElement;
        setIsOverflow(content.clientWidth > containerElement.clientWidth);
        setIsScrollStart(containerElement.scrollLeft === 0);
        setIsScrollEnd(
            containerElement.scrollLeft + containerElement.clientWidth >=
                containerElement.scrollWidth
        );
    }, []);

    useEffect(() => {
        const containerElement = containerRef.current;
        if (!containerElement) return;

        const onMouseDown = (e: globalThis.MouseEvent) => {
            dragState.current.isDown = true;
            containerElement.classList.add('active-drag');
            dragState.current.startX = e.pageX - containerElement.offsetLeft;
            dragState.current.scrollLeft = containerElement.scrollLeft;
        };

        const onMouseLeave = () => {
            dragState.current.isDown = false;
            containerElement.classList.remove('active-drag');
        };

        const onMouseUp = () => {
            dragState.current.isDown = false;
            containerElement.classList.remove('active-drag');
        };

        const onMouseMove = (e: globalThis.MouseEvent) => {
            if (!dragState.current.isDown) return;
            e.preventDefault();
            const x = e.pageX - containerElement.offsetLeft;
            const walk = (x - dragState.current.startX) * 2; // The multiplier makes scrolling faster
            containerElement.scrollLeft = dragState.current.scrollLeft - walk;
        };

        containerElement.addEventListener('scroll', handleScroll);
        containerElement.addEventListener('mousedown', onMouseDown);
        containerElement.addEventListener('mouseleave', onMouseLeave);
        containerElement.addEventListener('mouseup', onMouseUp);
        containerElement.addEventListener('mousemove', onMouseMove);

        handleScroll();

        return () => {
            containerElement.removeEventListener('scroll', handleScroll);
            containerElement.removeEventListener('mousedown', onMouseDown);
            containerElement.removeEventListener('mouseleave', onMouseLeave);
            containerElement.removeEventListener('mouseup', onMouseUp);
            containerElement.removeEventListener('mousemove', onMouseMove);
        };
    }, [isLandscape, containerRef.current]);

    useEffect(() => {
        if (isOverflow) {
            let scrollerCN = `scroll-content gt-${props.gameType}`;

            if (!isScrollStart && !isScrollEnd) {
                scrollerCN += ' scroller-shadow';
            } else {
                if (!isScrollStart) {
                    scrollerCN += ' scroller-left-shadow';
                } else if (!isScrollEnd) {
                    scrollerCN += ' scroller-right-shadow';
                }
            }
            setScrollerCN(scrollerCN);
            handleScroll();
        } else {
            setScrollerCN(`scroll-content gt-${props.gameType}`);
        }
    }, [isOverflow, isScrollStart, isScrollEnd]);

    return (
        <div className="bet-record-horizontal-scroller">
            <div className="arrow reverse">
                {!isScrollStart && <BetRecordScrollerArrow />}
            </div>
            <div
                className={scrollerCN}
                style={{
                    justifyContent: `${!isOverflow ? 'flex-end' : 'flex-start'}`,
                }}
                ref={containerRef}
            >
                {props.children}
            </div>
            <div className="arrow">
                {!isScrollEnd && <BetRecordScrollerArrow />}
            </div>
        </div>
    );
};
